import React, { useState, useEffect, useCallback } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { doc, getDoc, setDoc, Timestamp } from 'firebase/firestore';
import { db } from '../../utils/firebase';
import { collectionPath } from '../../utils/firestoreCollections';
import Tooltip from '../common/Tooltip';

/**
 * LogHealthPanel
 *
 * Admin UI for configuring log-collection size/age alert thresholds
 * and running an on-demand health check.
 *
 * Backend:
 *   - Scheduled: `monitorLogHealth` (daily, 6 AM EST)
 *   - Callable:  `triggerLogHealthCheck`
 *
 * Thresholds are stored in:
 *   - systemSettings/log_alerts          (global + enabled/emailAlerts flags)
 *   - systemSettings/log_alerts_{key}    (per-collection override)
 */
const LogHealthPanel = ({ user, isReadOnly, setToast }) => {
  const [config, setConfig] = useState({
    enabled: true,
    maxDocs: 100000,
    maxAgeDays: 400,
    alertEmails: [] // specific emails to alert; empty = email alerts off
  });
  const [configLoading, setConfigLoading] = useState(true);
  const [savingConfig, setSavingConfig] = useState(false);
  const [editing, setEditing] = useState({});
  const [emailInput, setEmailInput] = useState('');
  const [report, setReport] = useState(null);
  const [lastCheckedAt, setLastCheckedAt] = useState(null);
  const [checking, setChecking] = useState(false);

  const loadConfig = useCallback(async () => {
    try {
      const snap = await getDoc(doc(db, collectionPath('systemSettings'), 'log_alerts'));
      if (snap.exists()) {
        const data = snap.data();
        setConfig(prev => ({
          enabled: data.enabled !== false,
          maxDocs: data.maxDocs ?? prev.maxDocs,
          maxAgeDays: data.maxAgeDays ?? prev.maxAgeDays,
          alertEmails: Array.isArray(data.alertEmails) ? data.alertEmails : []
        }));
        if (data.lastReport) setReport(data.lastReport);
        if (data.lastCheckedAt?.toDate) setLastCheckedAt(data.lastCheckedAt.toDate());
      }
    } catch (err) {
      console.error('Failed to load log_alerts config:', err);
    } finally {
      setConfigLoading(false);
    }
  }, []);

  useEffect(() => { loadConfig(); }, [loadConfig]);

  const saveConfig = async (patch) => {
    setSavingConfig(true);
    try {
      const next = { ...config, ...patch };
      await setDoc(
        doc(db, collectionPath('systemSettings'), 'log_alerts'),
        {
          name: 'Log Health Alert Configuration',
          category: 'config',
          isActive: true,
          enabled: next.enabled,
          maxDocs: next.maxDocs,
          maxAgeDays: next.maxAgeDays,
          alertEmails: next.alertEmails,
          updatedAt: Timestamp.now(),
          updatedBy: user?.uid || '',
          updatedByName: user?.displayName || user?.email || ''
        },
        { merge: true }
      );
      setConfig(next);
      setToast({ message: 'Log health settings saved.', type: 'success' });
    } catch (err) {
      setToast({ message: `Failed to save: ${err.message}`, type: 'error' });
    } finally {
      setSavingConfig(false);
    }
  };

  const runHealthCheck = async () => {
    setChecking(true);
    try {
      const functions = getFunctions();
      const trigger = httpsCallable(functions, 'triggerLogHealthCheck');
      const result = await trigger({});
      setReport(result.data.healthReport || {});
      setLastCheckedAt(new Date(result.data.checkedAt));
      const breachCount = Object.values(result.data.healthReport || {})
        .filter(r => r.breaches && r.breaches.length > 0).length;
      setToast({
        message: breachCount > 0
          ? `Health check complete: ${breachCount} collection${breachCount !== 1 ? 's' : ''} over threshold.`
          : 'Health check complete: all collections within thresholds.',
        type: breachCount > 0 ? 'error' : 'success'
      });
    } catch (err) {
      console.error('Log health check failed:', err);
      setToast({ message: `Health check failed: ${err.message}`, type: 'error' });
    } finally {
      setChecking(false);
    }
  };

  const formatNumber = (n) => (n == null ? '—' : Number(n).toLocaleString());

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mt-6">
      <div className="flex items-start justify-between mb-4 gap-4 flex-wrap">
        <div>
          <h3 className="text-xl font-semibold text-gray-900">🩺 Log Health Monitoring</h3>
          <p className="text-sm text-gray-600 mt-1">
            Automated daily check (6 AM EST) alerts admins when any log collection exceeds size or age thresholds.
            Alerts appear in the notifications bell and optionally as email.
          </p>
        </div>
        {!isReadOnly && (
          <Tooltip text="Run a health check now (admin-only)">
            <button
              onClick={runHealthCheck}
              disabled={checking}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 text-sm font-medium"
            >
              {checking ? 'Checking…' : '▶ Run Check Now'}
            </button>
          </Tooltip>
        )}
      </div>

      {configLoading ? (
        <p className="text-sm text-gray-500 animate-pulse">Loading settings…</p>
      ) : (
        <div className="space-y-4">
          {/* Thresholds */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <ThresholdField
              label="Max Documents per Collection"
              hint="Alert when a single log collection exceeds this count"
              value={config.maxDocs}
              editing={editing.maxDocs != null ? editing.maxDocs : null}
              onEdit={() => setEditing({ ...editing, maxDocs: config.maxDocs })}
              onChange={(v) => setEditing({ ...editing, maxDocs: v })}
              onSave={() => {
                const parsed = parseInt(editing.maxDocs, 10);
                if (isNaN(parsed) || parsed < 100) {
                  setToast({ message: 'Max docs must be at least 100.', type: 'error' });
                  return;
                }
                saveConfig({ maxDocs: parsed });
                setEditing({});
              }}
              onCancel={() => setEditing({})}
              disabled={isReadOnly}
              saving={savingConfig}
              suffix="docs"
            />
            <ThresholdField
              label="Max Oldest Age (days)"
              hint="Alert when the oldest log in a collection exceeds this age"
              value={config.maxAgeDays}
              editing={editing.maxAgeDays != null ? editing.maxAgeDays : null}
              onEdit={() => setEditing({ ...editing, maxAgeDays: config.maxAgeDays })}
              onChange={(v) => setEditing({ ...editing, maxAgeDays: v })}
              onSave={() => {
                const parsed = parseInt(editing.maxAgeDays, 10);
                if (isNaN(parsed) || parsed < 1 || parsed > 1825) {
                  setToast({ message: 'Max age must be between 1 and 1825 days.', type: 'error' });
                  return;
                }
                saveConfig({ maxAgeDays: parsed });
                setEditing({});
              }}
              onCancel={() => setEditing({})}
              disabled={isReadOnly}
              saving={savingConfig}
              suffix="days"
            />
          </div>

          {/* Toggles + Email */}
          <div className="flex flex-col gap-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={config.enabled}
                onChange={e => saveConfig({ enabled: e.target.checked })}
                disabled={isReadOnly || savingConfig}
                className="h-4 w-4 text-indigo-600 rounded"
              />
              <span className="text-sm text-gray-700">Daily health check enabled</span>
            </label>
            <div className="w-full">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-sm font-medium text-gray-700">Email alerts (SendGrid)</span>
                <span className="text-xs text-gray-400">— add specific recipient addresses; leave empty to disable</span>
              </div>
              {!isReadOnly && (
                <div className="flex items-center gap-2">
                  <input
                    type="email"
                    value={emailInput}
                    onChange={e => setEmailInput(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        const trimmed = emailInput.trim().toLowerCase();
                        if (trimmed && !config.alertEmails.includes(trimmed)) {
                          saveConfig({ alertEmails: [...config.alertEmails, trimmed] });
                        }
                        setEmailInput('');
                      }
                    }}
                    placeholder="email@example.com and press Enter"
                    className="flex-1 border border-gray-300 rounded px-2 py-1 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    disabled={savingConfig}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      const trimmed = emailInput.trim().toLowerCase();
                      if (trimmed && !config.alertEmails.includes(trimmed)) {
                        saveConfig({ alertEmails: [...config.alertEmails, trimmed] });
                      }
                      setEmailInput('');
                    }}
                    disabled={!emailInput.trim() || savingConfig}
                    className="px-3 py-1 bg-indigo-600 text-white rounded text-sm hover:bg-indigo-700 disabled:opacity-40"
                  >
                    Add
                  </button>
                </div>
              )}
              {config.alertEmails.length === 0 ? (
                <p className="text-xs text-gray-400 mt-1">No alert emails configured — email notifications disabled.</p>
              ) : (
                <div className="flex flex-wrap gap-2 mt-2">
                  {config.alertEmails.map(email => (
                    <span key={email} className="flex items-center gap-1 bg-indigo-100 text-indigo-800 text-xs px-2 py-0.5 rounded-full">
                      {email}
                      {!isReadOnly && (
                        <button
                          type="button"
                          onClick={() => saveConfig({ alertEmails: config.alertEmails.filter(e => e !== email) })}
                          className="ml-1 text-indigo-500 hover:text-red-600 leading-none"
                          title="Remove"
                        >×</button>
                      )}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Last report */}
          {report && (
            <div className="mt-4">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-semibold text-gray-800">📊 Latest Health Report</h4>
                {lastCheckedAt && (
                  <span className="text-xs text-gray-500">
                    Last checked: {lastCheckedAt.toLocaleString()}
                  </span>
                )}
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="text-left px-3 py-2 font-medium text-gray-700">Collection</th>
                      <th className="text-right px-3 py-2 font-medium text-gray-700">Docs</th>
                      <th className="text-right px-3 py-2 font-medium text-gray-700">Oldest</th>
                      <th className="text-left px-3 py-2 font-medium text-gray-700">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {Object.entries(report).map(([key, info]) => {
                      const hasBreach = Array.isArray(info.breaches) && info.breaches.length > 0;
                      return (
                        <tr key={key} className={hasBreach ? 'bg-red-50' : ''}>
                          <td className="px-3 py-2 text-gray-800">{info.name || key}</td>
                          <td className="px-3 py-2 text-right tabular-nums">{formatNumber(info.docCount)}</td>
                          <td className="px-3 py-2 text-right tabular-nums">
                            {info.oldestAgeDays != null ? `${info.oldestAgeDays}d` : '—'}
                          </td>
                          <td className="px-3 py-2">
                            {info.error ? (
                              <span className="text-red-600 text-xs">Error: {info.error}</span>
                            ) : hasBreach ? (
                              <span className="text-red-700 text-xs">⚠️ {info.breaches.join('; ')}</span>
                            ) : (
                              <span className="text-green-700 text-xs">✓ OK</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const ThresholdField = ({ label, hint, value, editing, onEdit, onChange, onSave, onCancel, disabled, saving, suffix }) => {
  const isEditing = editing != null;
  return (
    <div className="p-3 bg-white rounded-lg border border-gray-200">
      <div className="mb-1">
        <span className="font-medium text-gray-800 text-sm">{label}</span>
        <p className="text-xs text-gray-500 mt-0.5">{hint}</p>
      </div>
      <div className="flex items-center gap-2">
        {isEditing ? (
          <>
            <input
              type="number"
              value={editing}
              onChange={e => onChange(e.target.value)}
              className="w-28 border border-gray-300 rounded px-2 py-1 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
            <span className="text-xs text-gray-500">{suffix}</span>
            <button
              onClick={onSave}
              disabled={saving}
              className="px-2 py-1 bg-indigo-600 text-white rounded text-xs hover:bg-indigo-700 disabled:opacity-50"
            >
              {saving ? '…' : 'Save'}
            </button>
            <button
              onClick={onCancel}
              className="px-2 py-1 text-gray-500 hover:text-gray-700 text-xs"
            >
              Cancel
            </button>
          </>
        ) : (
          <>
            <span className="text-lg font-semibold text-indigo-700 tabular-nums">
              {Number(value).toLocaleString()}
            </span>
            <span className="text-xs text-gray-500">{suffix}</span>
            {!disabled && (
              <button
                onClick={onEdit}
                className="ml-auto px-2 py-1 text-xs text-indigo-600 hover:bg-indigo-50 rounded transition-colors"
              >
                ✏️ Edit
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default LogHealthPanel;
